import { useEffect, useState } from 'react';

/**
 * Shared matchMedia subscriptions keyed by query string so multiple
 * consumers (e.g. several SlickCarousels) share one browser listener.
 */
const subscriptions = new Map();

function getMatchMedia(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  return window.matchMedia(query);
}

function subscribe(query, listener) {
  let entry = subscriptions.get(query);

  if (!entry) {
    const mql = getMatchMedia(query);
    if (!mql) {
      listener(false);
      return () => {};
    }

    entry = {
      mql,
      listeners: new Set(),
      matches: mql.matches,
    };

    entry.onChange = (event) => {
      entry.matches = event.matches;
      entry.listeners.forEach((notify) => notify(event.matches));
    };

    if (typeof entry.mql.addEventListener === 'function') {
      entry.mql.addEventListener('change', entry.onChange);
    } else {
      entry.mql.addListener(entry.onChange);
    }

    subscriptions.set(query, entry);
  }

  entry.listeners.add(listener);
  listener(entry.matches);

  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size > 0) return;

    if (typeof entry.mql.removeEventListener === 'function') {
      entry.mql.removeEventListener('change', entry.onChange);
    } else {
      entry.mql.removeListener(entry.onChange);
    }
    subscriptions.delete(query);
  };
}

/**
 * Reactively tracks a CSS media query via window.matchMedia.
 * @param {string} query e.g. '(max-width: 767px)'
 * @returns {boolean}
 */
export function useMatchMedia(query) {
  const [matches, setMatches] = useState(() => {
    const mql = getMatchMedia(query);
    return mql ? mql.matches : false;
  });

  useEffect(() => {
    return subscribe(query, setMatches);
  }, [query]);

  return matches;
}

/** Aligns with Tailwind `md` (min-width: 768px) — true below that. */
export const MOBILE_BELOW_MD_QUERY = '(max-width: 767px)';

export function useIsMobileBelowMd() {
  return useMatchMedia(MOBILE_BELOW_MD_QUERY);
}

export default useMatchMedia;
